import { useDrawer } from '@/components/common/DrawerManager';
import { FlexRow } from '@/components/common/Flex';
import GoalIconCircle from '@/components/goals/GoalIconCircle';
import { FlexProps } from '@chakra-ui/react';
import { ExternalFinancialEntity, getName } from '@common/financialEntities';
import { getInstitutionName } from '@common/react/financialEntities';
import { PAGE_MODE_MINE_COLOR } from '@common/react/pageMode';
import { isSingleplayer, useCurrentUser } from '@common/react/user';
import { FC } from 'react';
import { Button } from '../common/Button';
import {
    EntityViewHeader,
    EntityViewHeaderNotification,
} from '../common/EntityView';
import { ChevronRight } from '../common/icons';
import { Subheader } from '../common/Typography';
import { useGoal } from '../goals/helpers';
import { SingleAvatar } from '../users/UserAvatars';
import ChangesDescription, { ChangedFields } from './ChangesDescription';
import { FinancialEntityIcon } from './FinancialEntityIcon';

export interface FinancialEntityDetailHeaderProps extends FlexProps {
    financialEntity: ExternalFinancialEntity;
    changedFields: ChangedFields;
    isSaveLoading: boolean;
    isSaveDisabled: boolean;
    onCancelChanges: VoidFunction;
    onSave: VoidFunction;
}

const HEADER_BACKGROUND_COLOR = 'gray.50';
const JOINT_AVATAR_BACKGROUND_COLOR = 'gray.200';

const FinancialEntityDetailHeader: FC<FinancialEntityDetailHeaderProps> = ({
    financialEntity: fe,
    changedFields,
    isSaveLoading,
    isSaveDisabled,
    onCancelChanges,
    onSave,
    ...props
}) => {
    const currentUser = useCurrentUser();
    const institutionName = getInstitutionName(fe);
    const { showDrawer } = useDrawer();
    const { goal } = useGoal(fe.goalId);

    const isPrivate =
        fe.shareStatus === 'PRIVATE' && !isSingleplayer(currentUser);
    const isJoint = fe.ownershipType === 'JOINT';

    const avatarStyles = {
        size: 'sm',
        bg: isPrivate
            ? PAGE_MODE_MINE_COLOR
            : isJoint
              ? JOINT_AVATAR_BACKGROUND_COLOR
              : HEADER_BACKGROUND_COLOR,
    };

    return (
        <>
            <EntityViewHeader
                bg={isPrivate ? PAGE_MODE_MINE_COLOR : HEADER_BACKGROUND_COLOR}
                {...props}
            >
                <FlexRow align="center">
                    {goal && (
                        <>
                            <GoalIconCircle
                                type={goal.type}
                                mr={2}
                                opacity={0.4}
                                cursor="pointer"
                                onClick={() => {
                                    showDrawer({
                                        drawerKey: 'goal-detail',
                                        entityId: goal.id,
                                    });
                                }}
                                inverted={isPrivate}
                            />
                            <ChevronRight size="sm" mx={1} />
                        </>
                    )}

                    <FinancialEntityIcon
                        fe={fe}
                        mr={4}
                        mb={institutionName ? 0 : 1}
                    />
                    <Subheader color={isPrivate ? 'white' : 'textDark'}>
                        {getName(fe)}
                    </Subheader>
                </FlexRow>

                {isJoint ? (
                    <FlexRow
                        rounded="full"
                        bg={JOINT_AVATAR_BACKGROUND_COLOR}
                        p={1}
                    >
                        <SingleAvatar {...avatarStyles} />
                        <SingleAvatar isPartner {...avatarStyles} />
                    </FlexRow>
                ) : isPrivate ? (
                    <SingleAvatar withLock {...avatarStyles} />
                ) : fe.isMine ? (
                    <SingleAvatar {...avatarStyles} />
                ) : (
                    <SingleAvatar isPartner {...avatarStyles} />
                )}
            </EntityViewHeader>

            <EntityViewHeaderNotification
                isVisible={changedFields.length !== 0}
            >
                <ChangesDescription pr={4} changes={changedFields} />
                <FlexRow align="center">
                    <Button
                        variant="outline"
                        colorScheme="gray"
                        onClick={onCancelChanges}
                        onClickEvent="Financial Entity Cancel Changes Button Clicked"
                        mr={4}
                    >
                        Cancel
                    </Button>
                    <Button
                        isLoading={isSaveLoading}
                        disabled={isSaveDisabled}
                        onClick={onSave}
                        onClickEvent="Financial Entity Save Changes Button Clicked"
                        tabIndex={97}
                    >
                        Save changes
                    </Button>
                </FlexRow>
            </EntityViewHeaderNotification>
        </>
    );
};

export default FinancialEntityDetailHeader;
